import logger from '../log';
import HLS from 'hls-parser';
import { HttpResponse, HttpResponseMapper } from './interfaces';
import { Item } from '../webrtc/agent/interfaces';
import PlaybackConfig from '../config/PlaybackConfig';
import { SourceType, PlayType } from '../common/constants';
import filterDashBitrates from '../common/dash/filterBitrates';
import filterHlsBitrates from '../hls/filterHlsBitrates';
import { isManifestMime, isManifestUrl } from '../common/utils/sourceTypeUtils';
import { isString, maybeDecode } from '../common/utils/stringify';
import { itemsContainUrl } from './itemsContainUrl';
import { monitorManifest } from './objectId/HlsObjectIdMapper';
import { safeGet } from '../util/storage';

const log = logger.getLogger(__filename);

function handleManifest(
  // SRV-3982 - url is needed by the `monitorManifest function.
  url: string,
  data: string,
  playback: PlaybackConfig,
  sourceType: SourceType,
  playType: PlayType
) {
  switch (sourceType) {
    case SourceType.DASH:
      log.debug('filtering dash manifest:', { url, data, playback });
      return filterDashBitrates(data, playback);
    case SourceType.HLS:
      if (isHlsMediaManifest(data)) {
        return handleMonitoringHlsManifest(url, data);
      }
      // TODO: refactor so that monitorManifest is not in the filter function
      log.debug('filtering hls manifest:', { url, data, playback });
      return filterHlsBitrates(url, data, playback, playType);
    default:
      return data;
  }
}

const isManifest = (items: Item[], response: HttpResponse) => {
  const url = response.request.url;
  const mime = response.headers && response.headers.get('content-type');
  return (
    itemsContainUrl(items, url) ||
    (mime && isManifestMime(mime)) ||
    isManifestUrl(url)
  );
};

export const handleMonitoringHlsManifest = (
  url: string,
  data: string
): string => {
  // Only monitor manifests if the player uses IVS (this is to prevent parsing
  // huge media manifests for no reason.
  if (safeGet({ key: 'ksdk::usesIvs', log, default: false })) {
    try {
      monitorManifest(url, HLS.parse(data));
    } catch (e) {
      log.warn('Unable to parse IVS media manifest', e);
    }
  } else {
    log.debug('No need to modify manifest, not a master playlist');
  }
  return data;
};

/**
 * Determine if a manifest file is an HLS Media Manifest String, we check
 * EXT-X-TARGETDURATION and EXT-X-MEDIA-SEQUENCE because they are required
 * in an HLS media manifest, and they should show up early in the string
 * usually in the first 4 lines or so (within the first 100 to 200 characters
 * in a media playlist), this should still be extremely fast for master
 * playlists as it is an O(N) check.
 *
 * @param data - unparsed manifest string
 */
const isHlsMediaManifest = (data: string): boolean => {
  return data.search(/EXT-X-(TARGETDURATION|MEDIA-SEQUENCE)/) !== -1;
};

export default function maybeModifyManifest(
  items: Item[],
  playback: PlaybackConfig,
  sourceType: SourceType,
  playType: PlayType
): HttpResponseMapper {
  return response => {
    // if it's the manifest url, then it's likely the "data" is a string. But we don't want empty strings.
    if (
      // should be the manifest
      response.body &&
      // there should be data
      isManifest(items, response)
    ) {
      const { maybeEncode, data } = maybeDecode(response.body);
      if (isString(data)) {
        const body = handleManifest(
          response.request.url,
          data,
          playback,
          sourceType,
          playType
        );
        response.body = maybeEncode(body);
      } else {
        log.info(`Unable to modify manifest ${response.request.url}`);
      }
    }
    return response;
  };
}
